// src/useElementHasScrolled/index.ts
import { useLayoutEffect, useRef, useState } from "react";
import { usePrevious } from "@volvo-cars/react-utils";
function useElementHasScrolled({
  disabled = false,
  offsetThreshold = 0
} = {}) {
  const scrollElementRef = useRef(null);
  const cachedScrollElementRef = scrollElementRef;
  const currentScrollElement = cachedScrollElementRef.current;
  const [hasScrolledDown, setHasScrolledDown] = useState(false);
  const prevScrollElement = usePrevious(currentScrollElement);
  const scrollTicking = useRef(false);
  useLayoutEffect(() => {
    const updateScrollDirection = () => {
      var _a;
      scrollTicking.current = false;
      if (!currentScrollElement) {
        return;
      }
      const scrollY = (_a = currentScrollElement.scrollTop) != null ? _a : 0;
      if (scrollY === 0) {
        setHasScrolledDown(false);
        return;
      }
      if (scrollY < offsetThreshold) {
        setHasScrolledDown(false);
        return;
      }
      setHasScrolledDown(true);
    };
    const handleScroll = () => {
      if (!scrollTicking.current) {
        scrollTicking.current = true;
        window.requestAnimationFrame(updateScrollDirection);
      }
    };
    if (disabled) {
      return setHasScrolledDown(false);
    } else {
      window.requestAnimationFrame(updateScrollDirection);
    }
    if (currentScrollElement) {
      prevScrollElement && prevScrollElement.removeEventListener("scroll", handleScroll);
      currentScrollElement.removeEventListener("scroll", handleScroll);
      currentScrollElement.addEventListener("scroll", handleScroll);
    }
    return () => {
      currentScrollElement == null ? void 0 : currentScrollElement.removeEventListener("scroll", handleScroll);
    };
  }, [disabled, offsetThreshold, prevScrollElement, currentScrollElement]);
  return [cachedScrollElementRef, hasScrolledDown];
}

// src/useScrollLock/index.tsx
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState as useState2
} from "react";
import { useLayoutEffect as useLayoutEffect2 } from "@volvo-cars/react-layout-utils";

// src/getScrollBarWidth/index.ts
var cachedScrollBarWidth = void 0;
var getScrollBarWidth = () => {
  if (typeof window === "undefined")
    return 0;
  if (typeof cachedScrollBarWidth === "number") {
    return cachedScrollBarWidth;
  }
  const element = document.createElement("div");
  const { style } = element;
  style.display = "block";
  style.position = "absolute";
  style.width = "100px";
  style.height = "100px";
  style.left = "-999px";
  style.top = "-999px";
  style.overflow = "scroll";
  document.body.insertBefore(element, null);
  const { clientWidth } = element;
  if (clientWidth === 0) {
    document.body.removeChild(element);
    return 0;
  }
  cachedScrollBarWidth = 100 - clientWidth;
  document.body.removeChild(element);
  return cachedScrollBarWidth;
};

// src/useScrollLock/index.tsx
var sheet;
function getSheet() {
  if (!sheet && document && document.head) {
    const styleEl = document.createElement("style");
    document.head.appendChild(styleEl);
    sheet = styleEl.sheet;
  }
  return sheet;
}
var SetScrollLockContext = createContext(() => {
});
var IsScrollLockedContext = createContext(false);
function useScrollBarWidth() {
  const [scrollBarWidth, setScrollBarWidth] = useState2(getScrollBarWidth());
  useEffect(() => {
    if (scrollBarWidth > 0)
      return;
    const update = () => setScrollBarWidth(getScrollBarWidth());
    if (typeof ResizeObserver === "function") {
      const observer = new ResizeObserver(update);
      observer.observe(document.body);
      return () => observer.disconnect();
    } else {
      const timeout = setTimeout(update, 500);
      return () => clearTimeout(timeout);
    }
  }, [scrollBarWidth]);
  return scrollBarWidth;
}
function useScrollLock(enableScrollLock) {
  const setScrollLock = useContext(SetScrollLockContext);
  const scrollBarWidth = useScrollBarWidth();
  const getScrollLockStyles = useCallback((scrollBarWidth2) => `body {
      overflow: hidden !important;
      padding-right: ${scrollBarWidth2}px !important;
    }`, []);
  useLayoutEffect2(() => {
    if (!enableScrollLock)
      return;
    setScrollLock(true);
    const styles = getScrollLockStyles(scrollBarWidth);
    const sheet2 = getSheet();
    sheet2 == null ? void 0 : sheet2.insertRule(styles, 0);
    return () => {
      sheet2 == null ? void 0 : sheet2.deleteRule(0);
      setScrollLock(false);
    };
  }, [scrollBarWidth, setScrollLock, enableScrollLock, getScrollLockStyles]);
}
export {
  useElementHasScrolled,
  useScrollLock
};
